/**
 * LLM Logger Utility
 * 
 * Simple, structured logging for LLM API calls following industry best practices:
 * - Structured JSON logging for easy analysis
 * - Separate request/response logging with correlation IDs
 * - Token usage and cost tracking
 * - Performance metrics
 * - Optional local storage for development/debugging
 */

export interface LLMLogEntry {
  // Request metadata
  requestId: string;
  timestamp: string;
  type: 'request' | 'response' | 'error';
  
  // Context
  component: string;
  known: string;
  target: string;
  
  // Request data (only for request type)
  request?: {
    endpoint: string;
    method: string;
    anchorCount?: number;
    max?: number;
    inputTokensEstimate?: number;
  };
  
  // Response data (only for response type)
  response?: {
    status: 'success' | 'fallback' | 'error';
    responseTimeMs: number;
    picks?: number;
    generated?: number;
    outputTokensEstimate?: number;
    usedFallback?: boolean;
  };
  
  // Error data (only for error type)
  error?: {
    message: string;
    code?: string;
    stack?: string;
  };
  
  // Payload samples (for debugging)
  payload?: {
    input?: any;  // Sample of input data
    output?: any; // Sample of output data
  };
}

/**
 * LLM Logger class with configurable storage backends
 */
export class LLMLogger {
  private static instance: LLMLogger;
  private logs: LLMLogEntry[] = [];
  private readonly maxLogsInMemory = 100;
  private readonly enableConsoleLog: boolean;
  private readonly enableLocalStorage: boolean;
  private readonly storageKey = 'syntaxlens_llm_logs';
  
  constructor(options?: {
    enableConsole?: boolean;
    enableLocalStorage?: boolean;
  }) {
    this.enableConsoleLog = options?.enableConsole ?? (import.meta.env.DEV || false);
    this.enableLocalStorage = options?.enableLocalStorage ?? (import.meta.env.DEV || false);
    
    // Load existing logs from localStorage if enabled
    if (this.enableLocalStorage) {
      this.loadFromStorage();
    }
  }
  
  static getInstance(options?: { enableConsole?: boolean; enableLocalStorage?: boolean }): LLMLogger {
    if (!LLMLogger.instance) {
      LLMLogger.instance = new LLMLogger(options);
    }
    return LLMLogger.instance;
  }
  
  /**
   * Generate a unique request ID for correlation
   */
  generateRequestId(): string {
    return `llm_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
  
  /**
   * Log an LLM request
   */
  logRequest(params: {
    requestId: string;
    component: string;
    known: string;
    target: string;
    endpoint: string;
    anchorCount?: number;
    max?: number;
    payload?: any;
  }): void {
    const entry: LLMLogEntry = {
      requestId: params.requestId,
      timestamp: new Date().toISOString(),
      type: 'request',
      component: params.component,
      known: params.known,
      target: params.target,
      request: {
        endpoint: params.endpoint,
        method: 'POST',
        anchorCount: params.anchorCount,
        max: params.max,
        inputTokensEstimate: this.estimateTokens(params.payload)
      }
    };
    
    // Add payload sample in dev mode
    if (import.meta.env.DEV && params.payload) {
      entry.payload = {
        input: this.truncatePayload(params.payload)
      };
    }
    
    this.addLog(entry);
  }
  
  /**
   * Log an LLM response
   */
  logResponse(params: {
    requestId: string;
    component: string;
    known: string;
    target: string;
    responseTimeMs: number;
    status: 'success' | 'fallback' | 'error';
    picks?: number;
    generated?: number;
    usedFallback?: boolean;
    payload?: any;
  }): void {
    const entry: LLMLogEntry = {
      requestId: params.requestId,
      timestamp: new Date().toISOString(),
      type: 'response',
      component: params.component,
      known: params.known,
      target: params.target,
      response: {
        status: params.status,
        responseTimeMs: params.responseTimeMs,
        picks: params.picks,
        generated: params.generated,
        outputTokensEstimate: this.estimateTokens(params.payload),
        usedFallback: params.usedFallback
      }
    };
    
    // Add payload sample in dev mode
    if (import.meta.env.DEV && params.payload) {
      entry.payload = {
        output: this.truncatePayload(params.payload)
      };
    }
    
    this.addLog(entry);
  }
  
  /**
   * Log an error
   */
  logError(params: {
    requestId: string;
    component: string;
    known: string;
    target: string;
    error: Error | unknown;
  }): void {
    const entry: LLMLogEntry = {
      requestId: params.requestId,
      timestamp: new Date().toISOString(),
      type: 'error',
      component: params.component,
      known: params.known,
      target: params.target,
      error: {
        message: params.error instanceof Error ? params.error.message : String(params.error),
        stack: params.error instanceof Error ? params.error.stack : undefined
      }
    };
    
    this.addLog(entry);
  }
  
  /**
   * Add log entry and manage storage
   */
  private addLog(entry: LLMLogEntry): void {
    // Add to in-memory logs
    this.logs.push(entry);
    
    // Trim if exceeds max
    if (this.logs.length > this.maxLogsInMemory) {
      this.logs = this.logs.slice(-this.maxLogsInMemory);
    }
    
    // Console logging
    if (this.enableConsoleLog) {
      const style = entry.type === 'error' ? 'color: red' : 
                   entry.type === 'request' ? 'color: blue' : 
                   'color: green';
      console.log(
        `%c[LLM ${entry.type.toUpperCase()}]`,
        style,
        entry.requestId,
        entry
      );
    }
    
    // LocalStorage persistence
    if (this.enableLocalStorage) {
      this.saveToStorage();
    }
  }
  
  /**
   * Estimate token count (rough approximation)
   */
  private estimateTokens(data: any): number {
    if (!data) return 0;
    const str = typeof data === 'string' ? data : JSON.stringify(data);
    // Rough estimate: 1 token ≈ 4 characters
    return Math.ceil(str.length / 4);
  }
  
  /**
   * Truncate payload for storage (keep first 500 chars)
   */
  private truncatePayload(data: any): any {
    const str = typeof data === 'string' ? data : JSON.stringify(data);
    if (str.length <= 500) return data;
    
    if (typeof data === 'string') {
      return str.substring(0, 500) + '... [truncated]';
    }
    
    // For objects/arrays, try to preserve structure
    try {
      const truncated = JSON.parse(str.substring(0, 500) + '"}');
      return { ...truncated, _truncated: true };
    } catch {
      return str.substring(0, 500) + '... [truncated]';
    }
  }
  
  /**
   * Save logs to localStorage
   */
  private saveToStorage(): void {
    if (!this.enableLocalStorage) return;
    
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.logs));
    } catch (e) {
      // Storage full or other error
      console.warn('Failed to save LLM logs to localStorage:', e);
    }
  }
  
  /**
   * Load logs from localStorage
   */
  private loadFromStorage(): void {
    if (!this.enableLocalStorage) return;
    
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.logs = JSON.parse(stored);
      }
    } catch (e) {
      console.warn('Failed to load LLM logs from localStorage:', e);
    }
  }
  
  /**
   * Get all logs
   */
  getLogs(): LLMLogEntry[] {
    return [...this.logs];
  }
  
  /**
   * Get logs for a specific request
   */
  getLogsByRequestId(requestId: string): LLMLogEntry[] {
    return this.logs.filter(log => log.requestId === requestId);
  }
  
  /**
   * Get summary statistics
   */
  getStats(): {
    totalRequests: number;
    successRate: number;
    averageResponseTime: number;
    fallbackRate: number;
    errorRate: number;
    byComponent: Record<string, number>;
    byLanguagePair: Record<string, number>;
  } {
    const requests = this.logs.filter(l => l.type === 'request');
    const responses = this.logs.filter(l => l.type === 'response');
    const errors = this.logs.filter(l => l.type === 'error');
    
    const successResponses = responses.filter(r => r.response?.status === 'success');
    const fallbackResponses = responses.filter(r => r.response?.usedFallback);
    
    const avgResponseTime = responses.reduce((acc, r) => 
      acc + (r.response?.responseTimeMs || 0), 0) / (responses.length || 1);
    
    const byComponent: Record<string, number> = {};
    const byLanguagePair: Record<string, number> = {};
    
    requests.forEach(r => {
      byComponent[r.component] = (byComponent[r.component] || 0) + 1;
      const pair = `${r.known}→${r.target}`;
      byLanguagePair[pair] = (byLanguagePair[pair] || 0) + 1;
    });
    
    return {
      totalRequests: requests.length,
      successRate: successResponses.length / (responses.length || 1),
      averageResponseTime: Math.round(avgResponseTime),
      fallbackRate: fallbackResponses.length / (responses.length || 1),
      errorRate: errors.length / (requests.length || 1),
      byComponent,
      byLanguagePair
    };
  }
  
  /**
   * Export logs as NDJSON (newline-delimited JSON)
   */
  exportAsNDJSON(): string {
    return this.logs.map(log => JSON.stringify(log)).join('\n');
  }
  
  /**
   * Export logs as CSV
   */
  exportAsCSV(): string {
    const headers = [
      'requestId', 'timestamp', 'type', 'component', 'known', 'target',
      'status', 'responseTimeMs', 'picks', 'errorMessage'
    ];
    
    const rows = this.logs.map(log => [
      log.requestId,
      log.timestamp,
      log.type,
      log.component,
      log.known,
      log.target,
      log.response?.status || '',
      log.response?.responseTimeMs || '',
      log.response?.picks || '',
      log.error?.message || ''
    ]);
    
    return [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
  }
  
  /**
   * Clear all logs
   */
  clear(): void {
    this.logs = [];
    if (this.enableLocalStorage) {
      localStorage.removeItem(this.storageKey);
    }
  }
}

// Export singleton instance for convenience
export const llmLogger = LLMLogger.getInstance({
  enableConsole: import.meta.env.DEV,
  enableLocalStorage: import.meta.env.DEV
});